import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { isAdmin } from '../lib/supabase'
import { useToast } from '../components/Toast'
import InlineFeedPost from '../components/InlineFeedPost'

export default function PromptsPage() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [prompts, setPrompts] = useState([])
  const [activePrompt, setActivePrompt] = useState(null)
  const [responses, setResponses] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingResponses, setLoadingResponses] = useState(false)
  const [newPromptText, setNewPromptText] = useState('')
  const [addingPrompt, setAddingPrompt] = useState(false)
  const [editingPromptId, setEditingPromptId] = useState(null)
  const [editingPromptText, setEditingPromptText] = useState('')

  useEffect(() => { fetchPrompts() }, [])

  async function fetchPrompts() {
    const { data } = await supabase.from('prompts')
      .select('*, profiles!prompts_author_id_fkey(display_name)')
      .eq('active', true)
      .order('created_at', { ascending: false })
    setPrompts(data || [])
    if (data?.length > 0 && !activePrompt) {
      setActivePrompt(data[0])
      fetchResponses(data[0].id)
    }
    setLoading(false)
  }

  async function fetchResponses(promptId) {
    setLoadingResponses(true)
    const { data } = await supabase.from('posts')
      .select('*, profiles!posts_author_id_fkey(id, display_name, column_inches, is_voice)')
      .eq('prompt_id', promptId)
      .eq('status', 'published')
      .order('created_at', { ascending: false })
    setResponses(data || [])
    setLoadingResponses(false)
  }

  function selectPrompt(p) {
    setActivePrompt(p)
    fetchResponses(p.id)
  }

  async function deletePrompt(id) {
    if (!window.confirm('Delete this prompt? All responses will remain but will no longer be linked to it.')) return
    const { error } = await supabase.from('prompts').delete().eq('id', id)
    if (error) toast(error.message, 'error')
    else { toast('Prompt deleted.', 'success'); fetchPrompts() }
  }

  async function savePromptEdit(id) {
    if (!editingPromptText.trim()) return
    const { error } = await supabase.from('prompts').update({ body: editingPromptText.trim() }).eq('id', id)
    if (error) toast(error.message, 'error')
    else { toast('Prompt updated.', 'success'); setEditingPromptId(null); setEditingPromptText(''); fetchPrompts() }
  }

  async function addPrompt() {
    if (!newPromptText.trim()) return
    setAddingPrompt(true)
    const { error } = await supabase.from('prompts').insert({
      body: newPromptText.trim(), author_id: user.id, active: true,
    })
    if (error) toast(error.message, 'error')
    else { toast('Prompt added.', 'success'); setNewPromptText(''); fetchPrompts() }
    setAddingPrompt(false)
  }

  function respondToPrompt() {
    if (!user) { navigate('/auth'); return }
    navigate(`/write?prompt=${activePrompt.id}`)
  }

  return (
    <div style={s.page}>
      <div style={s.hero}>
        <p style={s.eyebrow}>Writing Prompts</p>
        <h1 style={s.title}>Something to write about.</h1>
        <p style={s.sub}>
          A prompt goes live every week. Anyone can respond. All responses collect under the prompt.
          Pick one that moves you — then write something worth reading.
        </p>
      </div>

      <div className="prompts-layout">
        {/* Prompt list */}
        <aside className="prompts-sidebar">
          <p style={s.sideLabel}>Active Prompts</p>
          {loading ? (
            <p style={s.muted}>Loading...</p>
          ) : prompts.length === 0 ? (
            <p style={s.muted}>No prompts yet.</p>
          ) : prompts.map(p => (
            <div key={p.id} style={{ marginBottom: '0.5rem' }}>
              {editingPromptId === p.id ? (
                <div style={{ border: '1px solid var(--accent)', borderRadius: 8, padding: '0.75rem' }}>
                  <textarea
                    className="form-textarea"
                    value={editingPromptText}
                    onChange={e => setEditingPromptText(e.target.value)}
                    style={{ minHeight: 60, fontSize: '0.85rem', marginBottom: '0.5rem' }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button className="btn btn-accent btn-sm" onClick={() => savePromptEdit(p.id)}>Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditingPromptId(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => selectPrompt(p)}
                  style={{ ...s.promptBtn, ...(activePrompt?.id === p.id ? s.promptBtnActive : {}), width: '100%', textAlign: 'left' }}
                >
                  <span style={s.promptBtnText}>{p.body}</span>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <span style={s.promptMeta}>{new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                    {isAdmin(profile?.role) && (
                      <div style={{ display: 'flex', gap: '0.3rem' }} onClick={e => e.stopPropagation()}>
                        <button
                          style={s.microBtn}
                          onClick={() => { setEditingPromptId(p.id); setEditingPromptText(p.body) }}
                          title="Edit prompt"
                        >✎</button>
                        <button
                          style={{ ...s.microBtn, color: 'var(--accent)' }}
                          onClick={() => deletePrompt(p.id)}
                          title="Delete prompt"
                        >✕</button>
                      </div>
                    )}
                  </div>
                </button>
              )}
            </div>
          ))}

          {isAdmin(profile?.role) && (
            <div style={s.addPrompt}>
              <p style={{ ...s.sideLabel, marginBottom: '0.5rem' }}>Add Prompt</p>
              <textarea
                className="form-textarea"
                value={newPromptText}
                onChange={e => setNewPromptText(e.target.value)}
                placeholder="Write a prompt..."
                style={{ minHeight: 72, fontSize: '0.85rem', marginBottom: '0.5rem' }}
              />
              <button className="btn btn-accent btn-sm" onClick={addPrompt} disabled={addingPrompt}>
                {addingPrompt ? 'Adding...' : 'Add Prompt'}
              </button>
            </div>
          )}
        </aside>

        {/* Responses */}
        <main className="prompts-main">
          {activePrompt ? (
            <>
              <div style={s.activePromptCard}>
                <p style={s.activePromptLabel}>This week's prompt</p>
                <p style={s.activePromptBody}>{activePrompt.body}</p>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '1rem' }}>
                  <button className="btn btn-accent" onClick={respondToPrompt}>
                    ✎ Write a Response
                  </button>
                  <span style={s.responseCount}>
                    {responses.length} response{responses.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {loadingResponses ? (
                <p style={s.muted}>Loading responses...</p>
              ) : responses.length === 0 ? (
                <div className="empty-state">
                  <p>No responses yet. Be the first.</p>
                </div>
              ) : (
                <div>
                  {responses.map(post => (
                    <InlineFeedPost key={post.id} post={post} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="empty-state"><p>Select a prompt to see responses.</p></div>
          )}
        </main>
      </div>
    </div>
  )
}

const s = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  hero: { padding: '3rem 3rem 2.5rem', borderBottom: '1px solid var(--rule)' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.6rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '0.75rem' },
  sub: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1rem', color: 'var(--muted)', lineHeight: 1.7, maxWidth: '60ch' },
  layout: { display: 'grid', gridTemplateColumns: '280px 1fr', minHeight: 'calc(100vh - 10rem)', alignItems: 'start' },
  sidebar: { borderRight: '1px solid var(--rule)', padding: '2rem 1.5rem', position: 'sticky', top: '4.5rem', maxHeight: 'calc(100vh - 4.5rem)', overflowY: 'auto' },
  sideLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.75rem' },
  promptBtn: { width: '100%', textAlign: 'left', background: 'none', border: '1px solid var(--rule)', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '0.5rem', cursor: 'pointer', transition: 'all 0.15s' },
  promptBtnActive: { background: 'rgba(200,64,26,0.08)', borderColor: 'var(--accent)' },
  promptBtnText: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.85rem', color: 'var(--ink)', display: 'block', marginBottom: '0.25rem', lineHeight: 1.5 },
  promptMeta: { fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)', letterSpacing: '0.06em' },
  addPrompt: { marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--rule)' },
  main: { padding: '2rem 3rem' },
  activePromptCard: { padding: '1.5rem 2rem', background: 'rgba(200,64,26,0.05)', border: '1.5px solid rgba(200,64,26,0.2)', borderRadius: 10, marginBottom: '2rem' },
  activePromptLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.75rem' },
  activePromptBody: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.2rem', fontWeight: 600, lineHeight: 1.5, color: 'var(--ink)' },
  responseCount: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '0.06em' },
  muted: { fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', color: 'var(--muted)' },
  microBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
    fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)',
    borderRadius: 3, lineHeight: 1,
  },
}
